/**
 * Documentation API — INTERNAL (x-api-key) contracts (SCH-1478).
 *
 *   GET    /internal/docs/pages
 *   POST   /internal/docs/pages
 *   GET    /internal/docs/pages/:idOrSlug
 *   PATCH  /internal/docs/pages/:idOrSlug
 *   PUT    /internal/docs/pages/:idOrSlug/publication
 *
 * WHY A SECOND SURFACE OVER THE SAME PAGES. Every write in `request.ts` is
 * reachable only with a super-admin Clerk JWT, which is a browser session an
 * agent or a script cannot mint. These five routes put the SAME use-cases
 * behind the internal API key so headless callers can author documentation.
 *
 * THE HANDLERS BEHIND THESE SHAPES ARE TRANSPORT ONLY. They resolve the
 * addressing, then call the identical use-cases the Clerk routes call — so
 * publishing still runs the one transaction that passes HTML through
 * `services/docs/render/sanitize-docs-html.ts`. There is exactly one path
 * through that boundary and this file must never become the reason there are
 * two.
 *
 * AN INTERNAL CALLER IS A CALLER WITH A KEY, NOT A TRUSTED CALLER. `content`
 * here is `DocsContentSchema`, byte-for-byte the vocabulary an author's editor
 * is held to. A relaxed variant "because the caller is ours" would make the
 * block vocabulary advisory, and the corpus a reader sees is the same corpus
 * either way.
 *
 * There is deliberately NO delete: `Doc` rows carry the reader's URL and a
 * version history, and unpublishing already takes a page off /docs reversibly.
 * A headless delete is the one operation whose blast radius nothing here can
 * undo.
 */

import { z } from 'zod'

import {
  DocSchema,
  DocSectionOrderSchema,
  DocSectionSchema,
  DocSlugSchema,
  DocVersionStatusSchema,
  DocVisibilitySchema
} from '../../schemas/entities/doc'
import { DocsContentSchema } from '../../schemas/entities/docs-content'
import { CreateDocRequestSchema, DocRenderedHtmlSchema, DocRendererVersionSchema } from './request'
import { CreateDocResponseSchema, ListDocsResponseSchema, UpdateDocDraftResponseSchema } from './response'

// ============================================
// Addressing
// ============================================

/**
 * A page is addressed by EITHER its `Doc.publicId` (a CUID) or its `slug`.
 *
 * The slug half is the point. An agent writing documentation knows
 * `communications/sending-a-message`; nobody knows the CUID, and requiring one
 * would mean a list call before every read and every edit. The resolver prefers
 * a `publicId` match, so the two namespaces cannot shadow each other.
 *
 * A slug holds the FULL multi-segment reader path, so the route that carries
 * this parameter is a WILDCARD (`/pages/*idOrSlug`) and the segments arrive
 * pre-split. The backend rejoins them; a caller sends the slug as it reads,
 * unencoded, and percent-encoding the slashes also works.
 *
 * Bounded by the slug column's 191 rather than left open: this value reaches a
 * unique-index lookup, and an unbounded string in a path is a free scan.
 */
export const InternalDocPageRefParamsSchema = z.object({
  idOrSlug: z.string().min(1).max(191)
})
export type InternalDocPageRefParams = z.infer<typeof InternalDocPageRefParamsSchema>

// ============================================
// List
// ============================================

/**
 * The authoring list, filterable. `status` and `section` are pushed into the
 * query the admin list already runs; `visibility` and `slug` narrow the result
 * afterwards — the corpus is tens of pages, and a second WHERE-clause shape in
 * a service two surfaces share is not worth owning for that.
 *
 * `slug` is an EXACT match, and is how a caller answers "does this page already
 * exist" before a create that would otherwise 409.
 */
export const InternalListDocPagesQuerySchema = z
  .object({
    /** Omitted = every page regardless of status. */
    status: DocVersionStatusSchema.optional(),
    /** Omitted = every section. */
    section: DocSectionSchema.optional(),
    /** Omitted = every visibility tier, link-shareable included. */
    visibility: DocVisibilitySchema.optional(),
    /** Exact slug match — an existence check, not a prefix search. */
    slug: DocSlugSchema.optional()
  })
  .strict()
export type InternalListDocPagesQuery = z.infer<typeof InternalListDocPagesQuerySchema>

/** Identical to the admin list's rows — one row shape, two transports. */
export const InternalListDocPagesResponseSchema = ListDocsResponseSchema
export type InternalListDocPagesResponse = z.infer<typeof InternalListDocPagesResponseSchema>

// ============================================
// Create
// ============================================

/**
 * The SAME body the Clerk create takes, aliased rather than restated. Two
 * declarations of one request is how the two surfaces drift on the field that
 * matters — here that would be `visibility`, which is required precisely so no
 * caller creates a page without an explicit audience decision.
 */
export const InternalCreateDocPageRequestSchema = CreateDocRequestSchema
export type InternalCreateDocPageRequest = z.infer<typeof InternalCreateDocPageRequestSchema>

export const InternalCreateDocPageResponseSchema = CreateDocResponseSchema
export type InternalCreateDocPageResponse = z.infer<typeof InternalCreateDocPageResponseSchema>

// ============================================
// Read one
// ============================================

/** The editor read: the page with BOTH versions resolved, drafts included. */
export const InternalGetDocPageResponseSchema = DocSchema
export type InternalGetDocPageResponse = z.infer<typeof InternalGetDocPageResponseSchema>

// ============================================
// Merge update
// ============================================

/**
 * A PARTIAL write, merged server-side against the stored row. An absent key is
 * left exactly as stored and `null` clears a nullable one — the two are
 * different instructions and nothing in the chain may collapse them.
 *
 * `slug` is ABSENT, unlike the Clerk variant. A rename is only ever legal
 * before a page's first publish, and a headless caller that renames by accident
 * has changed a reader's URL with no editor in front of it to notice. A page
 * created with the wrong slug is a create away from being right.
 */
export const InternalUpdateDocPageRequestSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    description: z.string().max(500).nullable().optional(),
    visibility: DocVisibilitySchema.optional(),
    section: DocSectionSchema.optional(),
    sectionOrder: DocSectionOrderSchema.optional(),
    /**
     * Omitted for a metadata-only edit. Present, it writes the page's DRAFT and
     * is held to the full block vocabulary — a rejection names the failing
     * blocks, because the caller is usually an agent that has to self-correct.
     */
    content: DocsContentSchema.optional()
  })
  .strict()
  .refine(value => Object.keys(value).length > 0, {
    message: 'Provide at least one field to update'
  })
export type InternalUpdateDocPageRequest = z.infer<typeof InternalUpdateDocPageRequestSchema>

export const InternalUpdateDocPageResponseSchema = UpdateDocDraftResponseSchema
export type InternalUpdateDocPageResponse = z.infer<typeof InternalUpdateDocPageResponseSchema>

// ============================================
// Publication (idempotent PUT)
// ============================================

/**
 * Publication as STATE, not as two verbs.
 *
 * `PUT { published: true }` promotes the page's draft; sent again with nothing
 * new to promote it succeeds and reports `changed: false`. `PUT
 * { published: false }` demotes the published version back to a draft; sent
 * against an already-unpublished page it likewise succeeds unchanged. A caller
 * retrying after a timeout cannot double-publish or discover a 409 it has no
 * way to interpret — which is the whole reason this replaced separate
 * publish/unpublish routes on this surface.
 *
 * The caller does NOT name the draft to promote. The Clerk route takes a
 * `draftPublicId` because a human editor is holding one; a headless caller
 * would have to read the page first purely to echo an id back, and a stale echo
 * is a publish of the wrong version.
 *
 * `renderedHtml` stays optional and stays UNTRUSTED. The backend has no React
 * and never renders blocks itself, so a caller that cannot render simply
 * publishes without a cache and readers fall back to the block JSON.
 */
export const InternalSetDocPagePublicationRequestSchema = z
  .object({
    published: z.boolean(),
    /** Sanitized on the way to storage; the same field the Clerk publish takes. */
    renderedHtml: DocRenderedHtmlSchema.optional(),
    /** Identifies the renderer, so a stale cache is queryable. Requires `renderedHtml`. */
    rendererVersion: DocRendererVersionSchema.optional()
  })
  .strict()
  .refine(value => value.rendererVersion === undefined || value.renderedHtml !== undefined, {
    message: 'rendererVersion requires renderedHtml',
    path: ['rendererVersion']
  })
  // An unpublish carries no render. Accepting one silently would let a caller
  // believe it had cached HTML for a page it just took off /docs.
  .refine(value => value.published || value.renderedHtml === undefined, {
    message: 'renderedHtml is only accepted when published is true',
    path: ['renderedHtml']
  })
export type InternalSetDocPagePublicationRequest = z.infer<typeof InternalSetDocPagePublicationRequestSchema>

/**
 * The page's publication state AFTER the call, plus whether this call is what
 * moved it. `changed: false` is a success: the state asked for was already the
 * state stored.
 */
export const InternalSetDocPagePublicationResponseSchema = z.object({
  publicId: z.string(),
  slug: z.string(),
  /** The state now in force — echoes the request on success. */
  published: z.boolean(),
  /** The live published version, or null on an unpublished page. */
  publishedVersionPublicId: z.string().nullable(),
  /** The page's working draft, or null when there is none. */
  draftPublicId: z.string().nullable(),
  publishedAt: z.string().datetime().nullable(),
  /** False when the page was already in the requested state and nothing was written. */
  changed: z.boolean()
})
export type InternalSetDocPagePublicationResponse = z.infer<typeof InternalSetDocPagePublicationResponseSchema>
